import React, { useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Scatter } from 'react-chartjs-2';
import { makeStyles } from "@mui/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box, Typography } from '@mui/material';

import {
  Chart as ChartJS,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  LinearScale,
  PointElement,
  LineElement,
} from 'chart.js';
import { fetchAnnualizedReturnsVolatility } from '../features/strategySlice';
import { getDeterministicColors } from './strategies/utils';
import NoDataMessage from './strategies/NoDataMessage';
import LoadingChart from './strategies/LoadingChart';
import { ExportButton } from './strategies/StrategyContent';

ChartJS.register(
  Title,
  ChartTooltip,
  Legend,
  LinearScale,
  PointElement,
  LineElement
);

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '10px',
  },
  chartContainer: {
    flex: 1,
    position: 'relative',
    width: '100%',
    minHeight: '300px',
  },
  canvas: {
    height: '100%',
    width: '100%',
  },
}));

const ScatterChart = ({ triggerResize, isScatterFullscreen, strategyId, assetType, annualized, perfStd, strategy, assetDetailsCache = {} }) => {
  const isLessThan1400px = useMediaQuery('(max-width:1400px)');
  const classes = useStyles({ isScatterFullscreen, isLessThan1400px });

  const dispatch = useDispatch();
  const chartRef = useRef(null);
  const data = useSelector(state => state.strategy.annualizedReturnsVolatility) || [];
  const status = useSelector(state => state.strategy.annualizedReturnsVolatilityStatus);
  const error = useSelector(state => state.strategy.annualizedReturnsVolatilityError);
  const settings = useSelector(state => state.strategy.settings);
  const calculated = useSelector(state => state.strategy.calculated);

  useEffect(() => {
    const resizeChart = () => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    };

    resizeChart();

    window.addEventListener('resize', resizeChart);

    return () => {
      window.removeEventListener('resize', resizeChart);
    };
  }, [triggerResize, isScatterFullscreen]);

  useEffect(() => {
    if (calculated && settings.startDate && settings.endDate && settings.currency && strategyId) {
      dispatch(fetchAnnualizedReturnsVolatility({
        id: strategyId,
        start_date: settings.startDate,
        end_date: settings.endDate,
        currency: settings.currency
      }));
    }
  }, [dispatch, calculated, settings, strategyId]);

  // Generate deterministic colors for the assets - always same colors for same strategy
  const deterministicColors = useMemo(() => {
    return data?.assets_annualized_returns_std ?
      getDeterministicColors(data.assets_annualized_returns_std.length, strategy?.id) : [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.assets_annualized_returns_std?.length, strategy?.id]);

  const chartData = useMemo(() => {
    const assets = data?.assets_annualized_returns_std || [];
    const isSingleAsset = assets.length === 1;

    // For single asset strategies, use asset data for strategy point to ensure they're at the same location
    let strategyData = [];
    if (isSingleAsset && assets.length > 0) {
      // Use the same coordinates as the asset for perfect overlap
      const asset = assets[0];
      strategyData = [{
        x: asset.annualized_returns_std.std * 100,
        y: asset.annualized_returns_std.annualized_returns * 100,
        label: strategy?.name || 'Strategy'
      }];
    } else if (perfStd && annualized) {
      // For multi-asset strategies, use performance data
      strategyData = [{ x: perfStd, y: annualized, label: strategy?.name || 'Strategy' }];
    }

    return {
      datasets: [
        {
          label: 'Assets',
          data: assets.map((asset, index) => {
            const ticker = asset.ticker;
            const cachedAsset = assetDetailsCache[ticker];

            let description = null;
            if (cachedAsset?.description && cachedAsset.description !== "undefined") {
              description = cachedAsset.description;
            } else if (asset.description && asset.description !== "undefined") {
              description = asset.description;
            }

            return {
              x: asset.annualized_returns_std.std * 100,
              y: asset.annualized_returns_std.annualized_returns * 100,
              label: description || ticker || `Asset ${index + 1}`,
              ticker: ticker,
              description: description,
              assetType: assetType[index] || 'Unknown'
            };
          }),
          backgroundColor: deterministicColors,
          borderColor: deterministicColors,
          borderWidth: 1,
          pointRadius: 5,
          pointHoverRadius: 8,
        },
        {
          label: 'Risk and Return point',
          data: strategyData,
          backgroundColor: '#00C851',
          borderColor: '#006600',
          borderWidth: 4,
          pointRadius: 14,
          pointHoverRadius: 18,
          pointStyle: 'star',
        },
      ],
    };
  }, [data?.assets_annualized_returns_std, deterministicColors, perfStd, annualized, strategy?.name, assetType, assetDetailsCache]);

  // Calculate dynamic scale limits based on actual data
  const assetDataPoints = data?.assets_annualized_returns_std?.map((asset, index) => ({
    x: asset.annualized_returns_std.std * 100,
    y: asset.annualized_returns_std.annualized_returns * 100,
  })) || [];

  // Get strategy data points from chartData for scale calculation
  const strategyDataPoints = chartData.datasets[1]?.data || [];
  const allDataPoints = [...assetDataPoints, ...strategyDataPoints];

  // Get all X and Y values for calculating limits
  const allXValues = allDataPoints.map(p => p.x).filter(x => typeof x === 'number' && !isNaN(x));
  const allYValues = allDataPoints.map(p => p.y).filter(y => typeof y === 'number' && !isNaN(y));

  // Calculate dynamic limits with fallbacks for empty data
  const maxX = allXValues.length > 0 ? Math.max(...allXValues) : 50;
  const minX = allXValues.length > 0 ? Math.min(...allXValues) : 0;
  const maxY = allYValues.length > 0 ? Math.max(...allYValues) : 10;
  const minY = allYValues.length > 0 ? Math.min(...allYValues) : -10;

  // Add padding (10% of range, minimum 5% points)
  const xRange = maxX - minX;
  const yRange = maxY - minY;
  const xPadding = Math.max(xRange * 0.1, 5);
  const yPadding = Math.max(yRange * 0.1, 2);

  // Final scale limits
  const xMin = Math.max(minX - xPadding, 0); // Don't go below 0 for volatility
  const xMax = maxX + xPadding;
  const yMin = minY - yPadding;
  const yMax = maxY + yPadding;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            const point = context[0].raw;

            if (point.label === strategy?.name) {
              return strategy?.name || 'Strategy';
            }

            if (point.description && point.ticker) {
              return `${point.description} (${point.ticker})`;
            } else {
              return point.ticker || point.label || 'Unknown Asset';
            }
          },
          label: function (context) {
            const point = context.raw;
            return [`Volatility: ${point.x.toFixed(2)}%`, `Return: ${point.y.toFixed(2)}%`];
          },
        },
        titleFont: {
          size: 16
        },
        bodyFont: {
          size: 16
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Volatility (%)',
          font: {
            size: 16,
            weight: 'bold',
          },
        },
        ticks: {
          callback: function(value) {
            return value.toFixed(1) + '%';
          }
        },
        min: xMin,
        max: xMax,
      },
      y: {
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Return (%)',
          font: {
            size: 16,
            weight: 'bold',
          },
        },
        ticks: {
          callback: function(value) {
            return value.toFixed(1) + '%';
          },
        },
        min: yMin,
        max: yMax,
      },
    },
  };

  if (!status || !data) {
    return <NoDataMessage message="Please click calculate button on settings." />;
  }

  return (
    <Box className={classes.container}>
      {status === 'loading' && <LoadingChart message="Loading risk & return data..." />}
      {status === 'failed' && (
        <Box sx={{ textAlign: 'center', padding: '20px' }}>
          <Typography color="error">Error loading Risk & Return data</Typography>
          <Typography variant="body2" color="textSecondary">
            {typeof error === 'string' ? error : 'The selected date range may not have complete data. Please try different dates.'}
          </Typography>
        </Box>
      )}
      {status === 'succeeded' && data && data.assets_annualized_returns_std && data.assets_annualized_returns_std.length > 0 && (
        <>
          <Box className={classes.headerContainer}>
            <ExportButton chartId="scatterChart" />
          </Box>
          <Box className={classes.chartContainer}>
            <div id="scatterChart" className={classes.canvas}>
              <Scatter ref={chartRef} data={chartData} options={options} />
            </div>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ScatterChart;